/*
 * DV CUSTOM — Room Viewer ("SEE IT IN YOUR HOME")
 * ------------------------------------------------------------------
 * Vanilla JS, pointer events + canvas 2D only. No dependencies.
 * Still-photo composite with manual scale calibration — not AR.
 * Copy/behavior locked per DaVallia-ArtViewer-Copy-Deck-2026-07-26.md (v2.3)
 * and DaVallia-ArtViewer-CC-Round6-Corrections-2026-07-26.md.
 *
 * All input is handled through pointer events (pointerdown/move/up/cancel),
 * which cover mouse, touch and stylus in one path — nothing here is gated
 * on touch support, so desktop and mobile run identical code.
 *
 * "Email it to me" and "Ask us about this piece" are UI-only in this
 * phase — there is no backend yet (Phase 2). Both show an honest
 * "not wired up yet" message on submit rather than a fake success.
 * ------------------------------------------------------------------
 */
(function () {
  'use strict';

  var DEFAULT_ART_FRACTION = 0.42;
  var TOUCH_MARKER_LIFT = 36; // px the calibration pin floats above a touch point
  var MIN_ART_WIDTH = 40;
  var MAX_INCHES_CHARS = 6;

  var SCALE_LABEL_BEFORE = 'Set Scale for Accurate Sizing';
  var SCALE_LABEL_AFTER = 'Adjust Scale';
  var BANNER_CONFIRM = 'Now at actual size. Drag to move it around the wall.';
  var BANNER_SETTLED = 'Shown at actual size.';

  /* Phase 2 backend — Cloudflare Worker (email-worker/) — sends the
     composite image via Resend and, if opted in, adds the customer to
     the DaVallia Circle via the Shopify Admin API. */
  var EMAIL_ENDPOINT = 'https://dv-room-viewer-email.thedavallia.workers.dev';

  /* Byline guard: these two vendor values must never render customer-facing
     (copy deck §7). Fill in the real display names when provided — inert
     until then. */
  var VENDOR_DISPLAY_ALIASES = {
    // 'Andrew Bereands': 'REPLACE_WITH_REAL_NAME',
    // 'Rowe': 'REPLACE_WITH_REAL_NAME'
  };
  function displayVendor(vendor) {
    return VENDOR_DISPLAY_ALIASES[vendor] || vendor;
  }

  function slugify(str) {
    return (str || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'artwork';
  }

  /* Safe no-op if GA4 isn't installed on the storefront — never assumes
     gtag/dataLayer exist. */
  function trackEvent(name, params) {
    if (typeof window.gtag === 'function') {
      window.gtag('event', name, params || {});
    } else if (window.dataLayer && typeof window.dataLayer.push === 'function') {
      window.dataLayer.push(Object.assign({ event: name }, params || {}));
    }
  }

  function init(root) {
    if (root.dataset.dvRvInited) return;
    root.dataset.dvRvInited = 'true';

    var artSrc = root.getAttribute('data-art-src');
    var artWIn = parseFloat(root.getAttribute('data-art-w-in')) || 0;
    var artHIn = parseFloat(root.getAttribute('data-art-h-in')) || 0;
    var artTitle = root.getAttribute('data-art-title') || '';
    var artFullTitle = root.getAttribute('data-art-full-title') || artTitle;
    var artVendor = root.getAttribute('data-art-vendor') || '';
    var artPrice = root.getAttribute('data-art-price') || '';
    var artUrl = root.getAttribute('data-art-url') || '';
    var aspect = artHIn > 0 ? (artWIn / artHIn) : 1;

    var openBtn = root.querySelector('[data-dv-rv-open]');
    var helpBtn = root.querySelector('[data-dv-rv-help]');
    var helpPopover = root.querySelector('[data-dv-rv-help-popover]');
    var modal = root.querySelector('[data-dv-rv-modal]');
    var closeEls = root.querySelectorAll('[data-dv-rv-close]');
    var stepEntry = root.querySelector('[data-dv-rv-step="entry"]');
    var stepStage = root.querySelector('[data-dv-rv-step="stage"]');
    var entryEl = root.querySelector('[data-dv-rv-entry]');
    var entryCard = root.querySelector('[data-dv-rv-entry-card]');
    var demoArt = root.querySelector('[data-dv-rv-demo-art]');
    var cameraBtn = root.querySelector('[data-dv-rv-camera-btn]');
    var fileCamera = root.querySelector('[data-dv-rv-file-camera]');
    var fileUpload = root.querySelector('[data-dv-rv-file-upload]');
    var stage = root.querySelector('[data-dv-rv-stage]');
    var roomImg = root.querySelector('[data-dv-rv-room-img]');
    var artWrap = root.querySelector('[data-dv-rv-art-wrap]');
    var artImg = root.querySelector('[data-dv-rv-art-img]');
    var banner = root.querySelector('[data-dv-rv-banner]');

    var calOverlay = root.querySelector('[data-dv-rv-cal]');
    var calPinL = root.querySelector('[data-dv-rv-cal-pin="l"]');
    var calPinR = root.querySelector('[data-dv-rv-cal-pin="r"]');
    var calLine = root.querySelector('[data-dv-rv-cal-line]');
    var scalePanel = root.querySelector('[data-dv-rv-scale-panel]');
    var calInches = root.querySelector('[data-dv-rv-cal-inches]');
    var calApplyBtn = root.querySelector('[data-dv-rv-cal-apply]');
    var calCancelBtn = root.querySelector('[data-dv-rv-cal-cancel]');
    var scaleToggleBtn = root.querySelector('[data-dv-rv-scale-toggle]');

    var emailPanel = root.querySelector('[data-dv-rv-email-panel]');
    var emailToggleBtn = root.querySelector('[data-dv-rv-email-toggle]');
    var emailInput = root.querySelector('[data-dv-rv-email-input]');
    var circleOptin = root.querySelector('[data-dv-rv-circle-optin]');
    var emailSendBtn = root.querySelector('[data-dv-rv-email-send]');
    var emailCancelBtn = root.querySelector('[data-dv-rv-email-cancel]');
    var emailMsg = root.querySelector('[data-dv-rv-email-msg]');

    var askPanel = root.querySelector('[data-dv-rv-ask-panel]');
    var askToggleBtn = root.querySelector('[data-dv-rv-ask-toggle]');
    var askName = root.querySelector('[data-dv-rv-ask-name]');
    var askEmail = root.querySelector('[data-dv-rv-ask-email]');
    var askMessage = root.querySelector('[data-dv-rv-ask-message]');
    var askSendBtn = root.querySelector('[data-dv-rv-ask-send]');
    var askCancelBtn = root.querySelector('[data-dv-rv-ask-cancel]');
    var askMsg = root.querySelector('[data-dv-rv-ask-msg]');

    var shareBtn = root.querySelector('[data-dv-rv-share]');
    var actionsBar = root.querySelector('[data-dv-rv-actions]');

    var pxPerInch = null;
    var calibrated = false;
    var roomObjectUrl = null;
    var lockedScrollY = 0;
    var bannerTimer = null;
    var calValue = '';

    var drag = { active: false, pointerId: null, startX: 0, startY: 0, origX: 0, origY: 0 };
    var resize = { active: false, pointerId: null, dir: '', startX: 0, startY: 0, origW: 0, origH: 0, origX: 0, origY: 0 };
    var calDrag = { active: false, pointerId: null, which: null };
    var pointers = {};
    var pinch = { active: false, startDist: 0, origW: 0, origH: 0, cx: 0, cy: 0 };

    artImg.src = artSrc;

    /* ---------- Camera capture: mobile only ----------
       `capture="environment"` on a desktop file input either does nothing
       or confuses the picker, so it is applied only where the pointer is
       coarse. The button is hidden outright only when the attribute is
       genuinely unsupported. */
    var coarsePointer = !!(window.matchMedia && window.matchMedia('(pointer: coarse)').matches);
    var supportsCapture = 'capture' in document.createElement('input');
    if (supportsCapture && coarsePointer) {
      fileCamera.setAttribute('capture', 'environment');
    }
    if (!supportsCapture && cameraBtn) {
      cameraBtn.setAttribute('hidden', '');
    }

    /* ---------- Visual viewport ---------- */
    function updateVh() {
      /* Only drives the mobile (<640px) height. The desktop breakpoint sets
         a definite 92vh of its own, so this never leaves the panel with an
         indefinite height. */
      if (window.visualViewport) {
        modal.style.setProperty('--dv-rv-vh', window.visualViewport.height + 'px');
      }
    }
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', updateVh);
    }

    /* ---------- Help popover ---------- */
    helpBtn.addEventListener('click', function (e) {
      e.stopPropagation();
      var isHidden = helpPopover.hasAttribute('hidden');
      if (isHidden) {
        helpPopover.removeAttribute('hidden');
        helpBtn.setAttribute('aria-expanded', 'true');
      } else {
        helpPopover.setAttribute('hidden', '');
        helpBtn.setAttribute('aria-expanded', 'false');
      }
    });
    document.addEventListener('click', function (e) {
      if (helpPopover.hasAttribute('hidden')) return;
      if (helpPopover.contains(e.target) || helpBtn.contains(e.target)) return;
      helpPopover.setAttribute('hidden', '');
      helpBtn.setAttribute('aria-expanded', 'false');
    });

    /* ---------- Modal open/close ----------
       iOS Safari does not fully honor `overflow:hidden` on body — the page
       behind a fixed overlay can still rubber-band, and focusing an input
       scrolls that live page instead of the overlay. Pinning body to
       `position:fixed` at the negative scroll offset removes anything for
       the browser to scroll. */
    function lockBodyScroll() {
      lockedScrollY = window.scrollY || window.pageYOffset || 0;
      document.body.style.position = 'fixed';
      document.body.style.top = '-' + lockedScrollY + 'px';
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
    }

    function unlockBodyScroll() {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      window.scrollTo(0, lockedScrollY);
    }

    function openModal() {
      resetToEntry();
      modal.removeAttribute('hidden');
      lockBodyScroll();
      updateVh();
      layoutDemoArt();
      trackEvent('viewer_opened', { product_title: artTitle, artist: artVendor });
      /* Card height isn't known until it has laid out. */
      requestAnimationFrame(function () {
        layoutDemoArt();
        clampEntryCard();
      });
    }

    function closeModal() {
      modal.setAttribute('hidden', '');
      unlockBodyScroll();
    }

    openBtn.addEventListener('click', openModal);
    closeEls.forEach(function (el) { el.addEventListener('click', closeModal); });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !modal.hasAttribute('hidden')) closeModal();
    });
    window.addEventListener('resize', function () {
      if (modal.hasAttribute('hidden')) return;
      layoutDemoArt();
      clampEntryCard();
    });

    function resetToEntry() {
      pxPerInch = null;
      calibrated = false;
      artWrap.classList.remove('dv-rv-art-wrap--locked');
      scaleToggleBtn.textContent = SCALE_LABEL_BEFORE;
      clearTimeout(bannerTimer);
      banner.setAttribute('hidden', '');
      banner.classList.remove('dv-rv-banner--confirm');
      closeAllPanels();
      stepStage.setAttribute('hidden', '');
      stepEntry.removeAttribute('hidden');
      fileCamera.value = '';
      fileUpload.value = '';
      if (roomObjectUrl) {
        URL.revokeObjectURL(roomObjectUrl);
        roomObjectUrl = null;
      }
    }

    function closeAllPanels() {
      calOverlay.setAttribute('hidden', '');
      scalePanel.setAttribute('hidden', '');
      emailPanel.setAttribute('hidden', '');
      askPanel.setAttribute('hidden', '');
      actionsBar.removeAttribute('hidden');
      stage.classList.remove('dv-rv-stage--compact');
    }

    /* Real native inputs (Email/Ask) trigger the iOS keyboard, unlike the
       calibration panel's own custom keypad. Shrinking the stage (CSS)
       makes room, but the browser's own "scroll focused input into view"
       doesn't reliably fire inside our locked-body/fixed-viewport setup —
       so nudge it explicitly once the keyboard has had a moment to open. */
    function focusIntoView(el) {
      setTimeout(function () {
        el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }, 300);
    }
    [emailInput, askName, askEmail, askMessage].forEach(function (el) {
      el.addEventListener('focus', function () { focusIntoView(el); });
    });

    /* ---------- Entry demo placement ---------- */
    function layoutDemoArt() {
      if (!entryEl || !demoArt) return;
      var w = entryEl.clientWidth;
      var h = entryEl.clientHeight;
      if (!w || !h) return;
      var maxWPct = parseFloat(root.getAttribute('data-demo-max-width')) || 32;
      var maxHPct = parseFloat(root.getAttribute('data-demo-max-height')) || 35;
      var boxW = w * (maxWPct / 100);
      var boxH = boxW / aspect;
      var maxH = h * (maxHPct / 100);
      if (boxH > maxH) {
        boxH = maxH;
        boxW = boxH * aspect;
      }
      demoArt.style.width = boxW + 'px';
      demoArt.style.height = boxH + 'px';
    }

    /* The card's vertical position is a merchant theme setting expressed as
       a percentage. Honour it, but never let it push the card out of view. */
    function clampEntryCard() {
      if (!entryEl || !entryCard) return;
      var frameH = entryEl.clientHeight;
      var cardH = entryCard.offsetHeight;
      if (!frameH || !cardH) return;
      var margin = 12;
      var top = entryCard.offsetTop;
      var maxTop = frameH - cardH - margin;
      var clamped = Math.max(margin, Math.min(top, maxTop));
      if (Math.abs(clamped - top) > 1) entryCard.style.top = clamped + 'px';
    }

    /* ---------- Room capture ---------- */
    fileCamera.addEventListener('change', handleFile);
    fileUpload.addEventListener('change', handleFile);

    function handleFile(e) {
      var file = e.target.files && e.target.files[0];
      if (!file) return;
      var method = e.target === fileCamera ? 'camera' : 'upload';
      if (roomObjectUrl) URL.revokeObjectURL(roomObjectUrl);
      roomObjectUrl = URL.createObjectURL(file);
      roomImg.onload = function () {
        /* A new photo invalidates any measurement taken in another room. */
        pxPerInch = null;
        calibrated = false;
        artWrap.classList.remove('dv-rv-art-wrap--locked');
        scaleToggleBtn.textContent = SCALE_LABEL_BEFORE;
        clearTimeout(bannerTimer);
        banner.setAttribute('hidden', '');
        banner.classList.remove('dv-rv-banner--confirm');

        stepEntry.setAttribute('hidden', '');
        stepStage.removeAttribute('hidden');
        layoutDefaultArt();
        trackEvent('viewer_photo_added', { product_title: artTitle, method: method });
      };
      roomImg.src = roomObjectUrl;
    }

    /* ---------- Artwork placement ---------- */
    function layoutDefaultArt() {
      var stageW = stage.clientWidth;
      var stageH = stage.clientHeight;
      var w = stageW * DEFAULT_ART_FRACTION;
      var h = w / aspect;
      var maxH = stageH * 0.7;
      if (h > maxH) {
        h = maxH;
        w = h * aspect;
      }
      setArtBox((stageW - w) / 2, (stageH - h) / 2, w, h);
    }

    function setArtBox(x, y, w, h) {
      artWrap.style.left = x + 'px';
      artWrap.style.top = y + 'px';
      artWrap.style.width = w + 'px';
      artWrap.style.height = h + 'px';
    }

    function getArtBox() {
      return {
        x: parseFloat(artWrap.style.left) || 0,
        y: parseFloat(artWrap.style.top) || 0,
        w: parseFloat(artWrap.style.width) || 0,
        h: parseFloat(artWrap.style.height) || 0
      };
    }

    /* ---------- Drag + pinch on the artwork ---------- */
    function pointerCount() { return Object.keys(pointers).length; }

    artWrap.addEventListener('pointerdown', function (e) {
      /* The corner brackets sit inside a 44x44 hit area; the event target
         may be the SVG or its path, so match the nearest handle ancestor. */
      if (e.target.closest && e.target.closest('.dv-rv-handle')) return;

      pointers[e.pointerId] = { x: e.clientX, y: e.clientY };

      if (pointerCount() === 2 && !calibrated) {
        drag.active = false;
        var ids = Object.keys(pointers);
        var a = pointers[ids[0]];
        var b = pointers[ids[1]];
        var box = getArtBox();
        pinch.active = true;
        pinch.startDist = Math.hypot(b.x - a.x, b.y - a.y) || 1;
        pinch.origW = box.w;
        pinch.origH = box.h;
        pinch.cx = box.x + box.w / 2;
        pinch.cy = box.y + box.h / 2;
        return;
      }

      if (pointerCount() === 1) {
        drag.active = true;
        drag.pointerId = e.pointerId;
        drag.startX = e.clientX;
        drag.startY = e.clientY;
        var b0 = getArtBox();
        drag.origX = b0.x;
        drag.origY = b0.y;
        artWrap.setPointerCapture(e.pointerId);
      }
    });

    artWrap.addEventListener('pointermove', function (e) {
      if (pointers[e.pointerId]) {
        pointers[e.pointerId].x = e.clientX;
        pointers[e.pointerId].y = e.clientY;
      }

      if (pinch.active && pointerCount() === 2) {
        var ids = Object.keys(pointers);
        var a = pointers[ids[0]];
        var b = pointers[ids[1]];
        var dist = Math.hypot(b.x - a.x, b.y - a.y) || 1;
        var ratio = dist / pinch.startDist;
        var newW = Math.max(MIN_ART_WIDTH, pinch.origW * ratio);
        var newH = newW / aspect;
        setArtBox(pinch.cx - newW / 2, pinch.cy - newH / 2, newW, newH);
        return;
      }

      if (!drag.active || e.pointerId !== drag.pointerId) return;
      var dx = e.clientX - drag.startX;
      var dy = e.clientY - drag.startY;
      artWrap.style.left = (drag.origX + dx) + 'px';
      artWrap.style.top = (drag.origY + dy) + 'px';
    });

    ['pointerup', 'pointercancel'].forEach(function (evt) {
      artWrap.addEventListener(evt, function (e) {
        delete pointers[e.pointerId];
        if (pointerCount() < 2) pinch.active = false;
        if (drag.active && e.pointerId === drag.pointerId) drag.active = false;
      });
    });

    /* ---------- Corner bracket resize — aspect locked, off after calibration ---------- */
    Array.prototype.forEach.call(root.querySelectorAll('.dv-rv-handle'), function (handle) {
      handle.addEventListener('pointerdown', function (e) {
        if (calibrated) return;
        e.stopPropagation();
        resize.active = true;
        resize.pointerId = e.pointerId;
        resize.dir = handle.getAttribute('data-dir');
        resize.startX = e.clientX;
        resize.startY = e.clientY;
        var box = getArtBox();
        resize.origW = box.w;
        resize.origH = box.h;
        resize.origX = box.x;
        resize.origY = box.y;
        handle.setPointerCapture(e.pointerId);
      });

      handle.addEventListener('pointermove', function (e) {
        if (!resize.active || e.pointerId !== resize.pointerId) return;
        var dx = e.clientX - resize.startX;
        var dy = e.clientY - resize.startY;
        var dir = resize.dir;

        var wFromH = resize.origW;
        if (dir.indexOf('e') !== -1) wFromH = resize.origW + dx;
        if (dir.indexOf('w') !== -1) wFromH = resize.origW - dx;

        var wFromV = resize.origW;
        if (dir.indexOf('s') !== -1) wFromV = (resize.origH + dy) * aspect;
        if (dir.indexOf('n') !== -1) wFromV = (resize.origH - dy) * aspect;

        var deltaH = Math.abs(wFromH - resize.origW);
        var deltaV = Math.abs(wFromV - resize.origW);
        var newW = Math.max(MIN_ART_WIDTH, deltaH >= deltaV ? wFromH : wFromV);
        var newH = newW / aspect;

        var x = resize.origX;
        var y = resize.origY;
        if (dir.indexOf('w') !== -1) x = resize.origX + (resize.origW - newW);
        if (dir.indexOf('n') !== -1) y = resize.origY + (resize.origH - newH);

        setArtBox(x, y, newW, newH);
      });

      ['pointerup', 'pointercancel'].forEach(function (evt) {
        handle.addEventListener(evt, function (e) {
          if (!resize.active || e.pointerId !== resize.pointerId) return;
          resize.active = false;
        });
      });
    });

    /* ---------- Scale mode ---------- */
    scaleToggleBtn.addEventListener('click', function () {
      var isOpen = !scalePanel.hasAttribute('hidden');
      closeAllPanels();
      if (isOpen) return;

      var stageW = stage.clientWidth;
      var stageH = stage.clientHeight;
      var y = stageH / 2;
      setPinPosition(calPinL, stageW * 0.3, y);
      setPinPosition(calPinR, stageW * 0.7, y);
      updateCalLine();
      actionsBar.setAttribute('hidden', '');
      calOverlay.removeAttribute('hidden');
      scalePanel.removeAttribute('hidden');
      /* Field starts empty and APPLY starts disabled — there is nothing to
         apply yet, so nothing is offered. No native keyboard ever opens. */
      calValue = '';
      scalePanel.classList.remove('dv-rv-panel--typing');
      renderCalValue();
      calInches.focus();
    });

    calCancelBtn.addEventListener('click', closeAllPanels);

    /* Delegated from the whole panel, not just the digit row: decimal and
       backspace sit next to the display, outside the single-row keypad,
       so the digit row can stay exactly one row of 1-9,0. */
    scalePanel.addEventListener('click', function (e) {
      var btn = e.target.closest('button[data-key]');
      if (!btn) return;
      var key = btn.getAttribute('data-key');

      if (key === 'back') {
        calValue = calValue.slice(0, -1);
      } else if (key === '.') {
        if (calValue.indexOf('.') === -1) calValue = calValue === '' ? '0.' : calValue + '.';
      } else if (calValue.length < MAX_INCHES_CHARS) {
        if (calValue === '0') calValue = key;
        else calValue += key;
      }
      renderCalValue();
      /* Keypad buttons steal focus on tap; pull it back to the display
         so the active-state border/glow/cursor stays visible while typing. */
      calInches.focus();
    });

    function renderCalValue() {
      calInches.value = calValue;
      var n = parseFloat(calValue);
      calApplyBtn.disabled = !(isFinite(n) && n > 0);
      /* Collapse the heading/instructions the moment there's a value to
         show — that's the point the customer needs the room photo, not
         more reading. Stays collapsed until the panel is reopened. */
      if (calValue.length > 0) {
        scalePanel.classList.add('dv-rv-panel--typing');
      }
    }

    calApplyBtn.addEventListener('click', applyScale);

    function applyScale() {
      var inches = parseFloat(calValue);
      if (!isFinite(inches) || inches <= 0) return;

      var lx = parseFloat(calPinL.style.left);
      var ly = parseFloat(calPinL.style.top);
      var rx = parseFloat(calPinR.style.left);
      var ry = parseFloat(calPinR.style.top);
      var pixelDist = Math.hypot(rx - lx, ry - ly);
      pxPerInch = pixelDist / inches;

      var trueW = artWIn * pxPerInch;
      var trueH = artHIn * pxPerInch;
      var box = getArtBox();
      var cx = box.x + box.w / 2;
      var cy = box.y + box.h / 2;
      setArtBox(cx - trueW / 2, cy - trueH / 2, trueW, trueH);

      calibrated = true;
      artWrap.classList.add('dv-rv-art-wrap--locked');
      /* The bar button must stop asking for what was just done. */
      scaleToggleBtn.textContent = SCALE_LABEL_AFTER;
      closeAllPanels();
      trackEvent('viewer_scale_set', { product_title: artTitle });

      clearTimeout(bannerTimer);
      banner.textContent = BANNER_CONFIRM;
      banner.classList.add('dv-rv-banner--confirm');
      banner.removeAttribute('hidden');
      bannerTimer = setTimeout(function () {
        banner.textContent = BANNER_SETTLED;
        banner.classList.remove('dv-rv-banner--confirm');
      }, 2600);
    }

    function setPinPosition(pin, x, y) {
      pin.style.left = x + 'px';
      pin.style.top = y + 'px';
    }

    function updateCalLine() {
      calLine.setAttribute('x1', parseFloat(calPinL.style.left));
      calLine.setAttribute('y1', parseFloat(calPinL.style.top));
      calLine.setAttribute('x2', parseFloat(calPinR.style.left));
      calLine.setAttribute('y2', parseFloat(calPinR.style.top));
    }

    [[calPinL, 'l'], [calPinR, 'r']].forEach(function (pair) {
      var pin = pair[0], which = pair[1];
      pin.addEventListener('pointerdown', function (e) {
        e.stopPropagation();
        calDrag.active = true;
        calDrag.pointerId = e.pointerId;
        calDrag.which = which;
        pin.setPointerCapture(e.pointerId);
      });
      pin.addEventListener('pointermove', function (e) {
        if (!calDrag.active || calDrag.which !== which || e.pointerId !== calDrag.pointerId) return;
        var rect = stage.getBoundingClientRect();
        var x = e.clientX - rect.left;
        var y = e.clientY - rect.top;
        /* Lift the marker above a touch fingertip so the customer can see
           exactly where it lands; the lifted point is what gets measured. */
        var lift = e.pointerType === 'touch' ? TOUCH_MARKER_LIFT : 0;
        setPinPosition(pin, x, y - lift);
        updateCalLine();
      });
      ['pointerup', 'pointercancel'].forEach(function (evt) {
        pin.addEventListener(evt, function (e) {
          if (calDrag.which === which && e.pointerId === calDrag.pointerId) calDrag.active = false;
        });
      });
    });

    /* ---------- Email it to me (UI only — Phase 2 wires the backend) ---------- */
    emailToggleBtn.addEventListener('click', function () {
      var isOpen = !emailPanel.hasAttribute('hidden');
      closeAllPanels();
      if (isOpen) return;
      actionsBar.setAttribute('hidden', '');
      emailMsg.setAttribute('hidden', '');
      emailInput.value = '';
      emailPanel.removeAttribute('hidden');
      stage.classList.add('dv-rv-stage--compact');
    });
    emailCancelBtn.addEventListener('click', closeAllPanels);

    var emailSending = false;
    var emailSendBtnLabel = emailSendBtn.textContent;

    emailSendBtn.addEventListener('click', function () {
      if (emailSending) return;

      var email = emailInput.value.trim();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        emailMsg.textContent = 'Enter a valid email address.';
        emailMsg.removeAttribute('hidden');
        emailInput.focus();
        return;
      }

      emailSending = true;
      emailSendBtn.disabled = true;
      emailSendBtn.textContent = 'Sending…';
      emailMsg.setAttribute('hidden', '');

      buildComposite(function (blob) {
        var optIn = circleOptin.checked;
        var body = new FormData();
        body.append('email', email);
        body.append('optIn', optIn ? 'true' : 'false');
        body.append('scaleSet', calibrated ? 'true' : 'false');
        body.append('title', artTitle);
        body.append('fullTitle', artFullTitle);
        body.append('vendor', artVendor);
        body.append('price', artPrice);
        body.append('url', artUrl);
        body.append('file', blob, compositeFilename());

        fetch(EMAIL_ENDPOINT + '/email', { method: 'POST', body: body })
          .then(function (res) {
            if (!res.ok) throw new Error('send failed');
            emailMsg.textContent = 'Sent! Check your inbox.';
            emailMsg.removeAttribute('hidden');
            trackEvent('viewer_emailed', { product_title: artTitle, circle_optin: optIn });
          })
          .catch(function () {
            emailMsg.textContent = 'Something went wrong sending that — please try again, or reach us directly.';
            emailMsg.removeAttribute('hidden');
          })
          .then(function () {
            emailSending = false;
            emailSendBtn.disabled = false;
            emailSendBtn.textContent = emailSendBtnLabel;
          });
      });
    });

    /* ---------- Ask us about this piece ---------- */
    askToggleBtn.addEventListener('click', function () {
      var isOpen = !askPanel.hasAttribute('hidden');
      closeAllPanels();
      if (isOpen) return;
      actionsBar.setAttribute('hidden', '');
      askMsg.setAttribute('hidden', '');
      askPanel.removeAttribute('hidden');
      stage.classList.add('dv-rv-stage--compact');
    });
    askCancelBtn.addEventListener('click', closeAllPanels);

    var askSending = false;
    var askSendBtnLabel = askSendBtn.textContent;

    askSendBtn.addEventListener('click', function () {
      if (askSending) return;

      var name = askName.value.trim();
      var email = askEmail.value.trim();
      var message = askMessage.value.trim();
      if (!name || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) || !message) {
        askMsg.textContent = 'Please fill in your name, a valid email, and a message.';
        askMsg.removeAttribute('hidden');
        return;
      }

      askSending = true;
      askSendBtn.disabled = true;
      askSendBtn.textContent = 'Sending…';
      askMsg.setAttribute('hidden', '');

      fetch(EMAIL_ENDPOINT + '/inquiry', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name,
          email: email,
          message: message,
          title: artTitle,
          fullTitle: artFullTitle,
          vendor: artVendor,
          price: artPrice,
          url: artUrl,
          wallPhoto: !!roomObjectUrl
        })
      })
        .then(function (res) {
          if (!res.ok) throw new Error('inquiry failed');
          askMsg.textContent = "Thanks — we'll be in touch.";
          askMsg.removeAttribute('hidden');
          trackEvent('viewer_inquiry', { product_title: artTitle });
        })
        .catch(function () {
          askMsg.textContent = 'Something went wrong sending that — please try again, or reach us directly.';
          askMsg.removeAttribute('hidden');
        })
        .then(function () {
          askSending = false;
          askSendBtn.disabled = false;
          askSendBtn.textContent = askSendBtnLabel;
        });
    });

    /* ---------- Save or Share (canvas composite) ---------- */
    function buildComposite(cb) {
      var roomNatW = roomImg.naturalWidth;
      var roomNatH = roomImg.naturalHeight;

      var stageRect = stage.getBoundingClientRect();
      var containScale = Math.min(stageRect.width / roomNatW, stageRect.height / roomNatH);
      var dispW = roomNatW * containScale;
      var dispH = roomNatH * containScale;
      var offsetX = (stageRect.width - dispW) / 2;
      var offsetY = (stageRect.height - dispH) / 2;
      var scale = roomNatW / dispW;

      var box = getArtBox();
      var paintX = (box.x - offsetX) * scale;
      var paintY = (box.y - offsetY) * scale;
      var paintW = box.w * scale;
      var paintH = box.h * scale;

      var canvas = document.createElement('canvas');
      canvas.width = roomNatW;
      canvas.height = roomNatH;
      var ctx = canvas.getContext('2d');
      ctx.drawImage(roomImg, 0, 0, roomNatW, roomNatH);

      ctx.save();
      ctx.shadowColor = 'rgba(0,0,0,0.40)';
      ctx.shadowBlur = 24 * scale;
      ctx.shadowOffsetY = 6 * scale;
      ctx.drawImage(artImg, paintX, paintY, paintW, paintH);
      ctx.restore();

      ctx.save();
      ctx.shadowColor = 'rgba(0,0,0,0.25)';
      ctx.shadowBlur = 8 * scale;
      ctx.shadowOffsetY = 2 * scale;
      ctx.drawImage(artImg, paintX, paintY, paintW, paintH);
      ctx.restore();

      ctx.drawImage(artImg, paintX, paintY, paintW, paintH);

      /* Caption line 1 keeps the FULL product title — dimensions live there
         and customers scan for them (copy deck §7). */
      var line1 = artFullTitle + ' | ' + displayVendor(artVendor) + ' | ' + artPrice;
      var line2 = 'DaVallia Gallery | 802.875.8900 | gallery@thedavallia.com';

      var fontSize = Math.max(14, Math.round(roomNatW * 0.02));
      var lineGap = Math.round(fontSize * 1.5);
      var barH = lineGap * 2 + Math.round(fontSize * 0.8);

      var labeled = document.createElement('canvas');
      labeled.width = roomNatW;
      labeled.height = roomNatH + barH;
      var lctx = labeled.getContext('2d');
      lctx.drawImage(canvas, 0, 0);
      lctx.fillStyle = '#000000';
      lctx.fillRect(0, roomNatH, roomNatW, barH);

      lctx.font = '400 ' + fontSize + 'px system-ui, -apple-system, sans-serif';
      lctx.fillStyle = 'rgba(255,255,255,0.92)';
      lctx.textAlign = 'center';
      lctx.textBaseline = 'middle';
      lctx.fillText(line1, roomNatW / 2, roomNatH + lineGap * 0.85);
      lctx.font = '400 ' + Math.round(fontSize * 0.82) + 'px system-ui, -apple-system, sans-serif';
      lctx.fillStyle = 'rgba(255,255,255,0.75)';
      lctx.fillText(line2, roomNatW / 2, roomNatH + lineGap * 1.85);

      labeled.toBlob(function (blob) { cb(blob); }, 'image/jpeg', 0.85);
    }

    function compositeFilename() {
      return slugify(artTitle) + '-davallia.jpg';
    }

    function downloadBlob(blob) {
      var url = URL.createObjectURL(blob);
      var a = document.createElement('a');
      a.href = url;
      a.download = compositeFilename();
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(function () { URL.revokeObjectURL(url); }, 4000);
    }

    /* No web page on iOS can write to the camera roll; the only route is the
       native share sheet's own "Save Image", which needs a `files` payload.
       Where file sharing is unsupported (Firefox, some desktop Chrome) fall
       back to a download rather than a link-only share, which would leave
       the customer no way to keep the image. */
    shareBtn.addEventListener('click', function () {
      /* Anonymous, aggregate-only alert — invisible to the customer, so
         failures are swallowed and never block the actual save/share. */
      fetch(EMAIL_ENDPOINT + '/share', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: artTitle, fullTitle: artFullTitle, vendor: artVendor, price: artPrice, url: artUrl })
      }).catch(function () {});
      trackEvent('viewer_shared', { product_title: artTitle });

      buildComposite(function (blob) {
        var displayName = displayVendor(artVendor);
        var shareText = 'Here’s ' + artTitle + ' by ' + displayName + ', previewed on the wall.\n\n—\n' +
          artTitle + ' · ' + displayName + ' · ' + artPrice + '\n' +
          'DaVallia Art and Interiors · Chester, Vermont · 802.875.8900';
        var shareTitle = artTitle + ' — on the wall';

        var file = null;
        try {
          file = new File([blob], compositeFilename(), { type: 'image/jpeg' });
        } catch (err) {
          file = null;
        }

        if (file && navigator.canShare && navigator.canShare({ files: [file] })) {
          navigator.share({ title: shareTitle, text: shareText, url: artUrl, files: [file] }).catch(function () {
            downloadBlob(blob);
          });
        } else {
          downloadBlob(blob);
        }
      });
    });
  }

  function initAll() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-dv-room-viewer]'), init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
